import { LinearProgress, Typography } from '@mui/material';
import React from 'react';
import type { CompData, CompSubString, CompTakeData } from '../RecordingSessionDataGrid/types';

type ColoredSubstring = {
    begin: number
    end: number
    color: string
}

const splitParts = (text: string, takes: CompTakeData[], colorMap: Record<string, string>) => {
    const formatted: ColoredSubstring[] = [];
    let unformatted: CompSubString[] = [{ begin: 0, end: text.length }];

    for (const take of takes) {
        const substrings = take.subStrings;
        const color = colorMap[take.takeId];

        for (const subStr of substrings) {
            if (subStr.begin > subStr.end) {
                console.error('begin is greater than end');
                continue;
            }

            formatted.push({ ...subStr, color });
            unformatted = unformatted.reduce((acc, a) => {
                // if no overlap return original
                if (subStr.end <= a.begin || subStr.begin >= a.end) {
                    acc.push(a);
                    return acc;
                }

                // if subStr overlaps perfectly with a
                if (subStr.begin === a.begin && subStr.end === a.end) return acc;

                // if subStr begins at the start of a
                if (subStr.begin === a.begin) {
                    acc.push({ begin: subStr.end, end: a.end });
                    return acc;
                }

                // if subStr ends at the end of a
                if (subStr.end === a.end) {
                    acc.push({ begin: a.begin, end: subStr.begin });
                    return acc;
                }

                acc.push({ begin: a.begin, end: subStr.begin }, { begin: subStr.end, end: a.end });
                return acc;
            }, [] as CompSubString[]);
        }
    }

    return [...formatted, ...unformatted]
        .sort((a, b) => a.begin - b.begin)
        .reduce((acc, cur) => {
            if (Object.keys(cur).includes('color')) {
                acc.push(
                    <span
                        key={cur.begin}
                        style={{ backgroundColor: (cur as ColoredSubstring).color, userSelect: 'none' }}
                        data-endpos={cur.end}
                    >
                        {text.slice(cur.begin, cur.end)}
                    </span>
                );
            } else {
                acc.push(text.slice(cur.begin, cur.end)); // javascript slice is dumb and inconsistent (and keeping the substring begin/end exclusive is easier)
            }
            return acc;
        }, [] as (string | JSX.Element)[]);
}

type Props = {
    text: string
    lineNo?: string
    loading: boolean
    comp?: Pick<CompData, 'takes'>
    colorMap?: Record<string, string>
}

export default function CompText({ text, lineNo, loading, comp, colorMap }: Props) {
    if (loading) return <LinearProgress />

    const parts = splitParts(text, comp?.takes ?? [], colorMap ?? {});

    return (
        <>
            {lineNo && <Typography>Line {lineNo}<br />{parts}</Typography>}
            {!lineNo && <Typography>{parts}</Typography>}
        </>
    );
}